import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import {
    AuthenticationService,
    Color,
    DateFormat,
    forLifeOf,
    LifecycleAware,
    Masks,
    Orientation,
    Unmasks,
    Icons,
    LsqValidators,
    PasswordI18nValidator,
    ForgotPasswordModal,
    ModalService
} from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { first } from 'rxjs/operators';

@LifecycleAware()
@Component({
    selector: 'reset-password',
    template: `
        <div class="section-header">
            <h2 class="section-heading h3">
                Reset Password
            </h2>
        </div>
        <form *ngIf="formGroup" (submit)="handleSubmit()" [formGroup]="formGroup">
            <div class="settings">
                <div *ngIf="!!failedResetMessage" class="row">
                    <div class="col-12 failed-reset">
                        <span i18n="Error label">Error:</span> {{ failedResetMessage }}
                    </div>
                </div>
                <p class="instructions-copy" i18n>
                    Change your password or recover your current one.
                </p>
                <lsq-input
                    type="password"
                    id="oldPassword"
                    name="oldPassword"
                    i18n-label="Label for the 'Old Password' input"
                    label="Old Password"
                    formControlName="oldPassword"
                    containerClass="mt-3"
                    [group]="formGroup"
                    [floatLabel]="true"
                >
                </lsq-input>
                <div class="forgot-pass-link">
                    <lsq-button
                        appearance="transparent"
                        (buttonClick)="handleForgotPassword()"
                        i18n="Reset password link"
                    >
                        Forgot your password?
                    </lsq-button>
                </div>
                <p class="password-requirements-copy" i18n="Password requirements">
                    Passwords must be at least 8 characters, contain one number, one capital letter,
                    one lowercase letter, and one special character.
                </p>
                <lsq-input
                    type="password"
                    id="password"
                    name="password"
                    i18n-label="Password input"
                    label="Password"
                    formControlName="password"
                    containerClass="mt-2"
                    [group]="formGroup"
                    [floatLabel]="true"
                >
                </lsq-input>
                <lsq-input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    i18n-label="Confirm password input"
                    label="Confirm Password"
                    formControlName="confirmPassword"
                    [group]="formGroup"
                    [floatLabel]="true"
                >
                </lsq-input>
                <lsq-button
                    type="submit"
                    buttonClass="btn-md"
                    [disabled]="isSubmitting"
                    [submitting]="isSubmitting"
                    [group]="formGroup"
                    i18n="Change password button"
                >
                    Change Password
                </lsq-button>
            </div>
        </form>
    `,
    styleUrls: ['./reset-password.component.scss']
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
    public formGroup: FormGroup;
    public isSubmitting = false;
    public failedResetMessage: string;

    // Constants.
    public dateFormat = DateFormat;
    public orientation = Orientation;
    public icons = Icons;
    public color = Color;
    public masks = Masks;
    public unmasks = Unmasks;

    public resetPasswordSuccessMessage = this._i18n({
        value: 'Your password has been successfully updated.',
        meaning: 'Reset password success message'
    });
    public resetPasswordOldPassMatchesError = this._i18n({
        value: 'This field must not match the previous password field.',
        meaning: 'Error message for when new password matches old one'
    });
    public resetPasswordPassNotMatchingError = this._i18n({
        value: '"Password" and "Confirm Password" must match.',
        meaning: 'Error message for when confirm password does not match password field'
    });

    constructor(
        private _alertService: AlertService,
        private _userService: UserService,
        private _modalService: ModalService,
        private _authenticationService: AuthenticationService,
        private _i18n: I18n,
        private _passwordI18nValidator: PasswordI18nValidator
    ) {}

    public ngOnInit(): void {
        this._createFormGroup();
    }

    public ngOnDestroy(): void {}

    public async handleForgotPassword(): Promise<void> {
        const user = await this._userService.user$.pipe(first()).toPromise();

        this._authenticationService
            .sendForgotPasswordEmail(user.emailAddress)
            .then(() => this._openForgotPasswordModal())
            .catch((errorMessage) => this._alertService.error(errorMessage));
    }

    public async handleSubmit(): Promise<void> {
        this.isSubmitting = true;
        const user = await this._userService.user$.pipe(first()).toPromise();

        const result = await this._authenticationService.resetPasswordUserInitiated(
            user.emailAddress,
            this.formGroup.get('oldPassword').value,
            this.formGroup.get('confirmPassword').value
        );
        this.isSubmitting = false;
        if (result.authorized) {
            delete this.failedResetMessage;
            this._alertService.success(this.resetPasswordSuccessMessage);
            this.formGroup.reset();
        } else {
            this.failedResetMessage = result.message;
        }
    }

    private _openForgotPasswordModal() {
        this._modalService.showModal(new ForgotPasswordModal());
    }

    private _createFormGroup(): void {
        this.formGroup = new FormGroup({
            oldPassword: new FormControl('', [Validators.required]),
            password: new FormControl('', [
                Validators.required,
                this._passwordI18nValidator.strictPassword(),
                LsqValidators.notMatchValue(
                    () => this.getOldPassword(),
                    this.resetPasswordOldPassMatchesError
                ),
                LsqValidators.matchValueSource(
                    () => this.formGroup?.get('confirmPassword'),
                    this.resetPasswordPassNotMatchingError
                )
            ]),
            confirmPassword: new FormControl('', [
                Validators.required,
                LsqValidators.matchValueConfirm(
                    () => this.formGroup?.get('password'),
                    this.resetPasswordPassNotMatchingError
                )
            ])
        });

        this.formGroup
            .get('oldPassword')
            .valueChanges.pipe(forLifeOf(this))
            .subscribe(() => {
                // Ensure that we update the `password` validation when the `oldPassword` changes for immediate feedback.
                this.formGroup.get('password').updateValueAndValidity();
            });
    }

    public getOldPassword(): string {
        if (this.formGroup && this.formGroup.get('oldPassword')) {
            return this.formGroup.get('oldPassword').value;
        }
        return '';
    }
}
